import { z } from 'zod';
/**
 * Ask — the reactive synthesis surface. The CEO types a plain-English
 * question; the agent streams back a cited synthesis with provenance +
 * confidence. Mirrors the OpenAI/Anthropic chat shape but pinned to the
 * LeanOpx ground rules: every claim is anchored to a connector source
 * via `[CITE-<id>]` markers the client renders through `<Annotated>`.
 *
 * Two collections, both server-mediated through `/api/orgs/:o/ask*`:
 *
 *   - `askThreads/{threadId}` — one row per conversation. Per-user
 *     ownership (ownerUid + orgId) so reads gate on author identity
 *     the same way notes do.
 *   - `askMessages/{messageId}` — one row per message in the
 *     conversation. Ordered by `createdAt` ASC for a single thread.
 *     Both user prompts AND agent responses live here.
 *
 * Multi-tenant: every doc carries `orgId`. The Firestore rules layer
 * mirrors the API gate — a hostile client can't enumerate another
 * user's threads even if they bypass the API.
 *
 * SSE wire format (server → client). Every frame is a `SseEvent`
 * envelope (`{ type, payload }`) — `writeSseEvent()` (in
 * `apps/functions/src/http/sse.ts`) emits `data: <full-envelope-json>`,
 * NOT a bare payload. Client parsers should `JSON.parse(data)` and read
 * `.type` + `.payload`:
 *   - `event: thread`   `data: {"type":"thread","payload":{"threadId":"...","title":"..."}}`     — emitted once on stream open
 *   - `event: token`    `data: {"type":"token","payload":{"text":"..."}}`                       — streaming chunk
 *   - `event: cites`    `data: {"type":"cites","payload":{"cites":[...]}}`                      — once on completion
 *   - `event: done`     `data: {"type":"done","payload":{"messageId":"...","confidence?":...}}` — final marker
 *   - `event: error`    `data: {"type":"error","payload":{"error":"..."}}`                      — terminal failure
 *   - `event: close`    `data: {}`                                                              — connection-close marker (from `closeSseStream`)
 */
export const ASK_THREADS_COLLECTION = 'askThreads';
export const ASK_MESSAGES_COLLECTION = 'askMessages';
/** Per-message caps. */
export const ASK_QUESTION_MAX = 4_000;
export const ASK_RESPONSE_MAX = 100_000;
export const ASK_THREAD_TITLE_MAX = 200;
export const ASK_CITE_LABEL_MAX = 200;
export const ASK_CITES_MAX = 32;
/**
 * Source citation kind. `connector` covers tool-grounded facts (Slack
 * thread, Salesforce deal, Stripe charge); `doc` covers research-style
 * citations (a brief, an investigation, a notes attachment) we may add
 * later. Mirrors the connector-vs-research split the UI primitive
 * library already encodes (`<SourceChip>` vs `<SourceBadge>`).
 */
export const AskCiteKind = z.enum(['connector', 'doc']);
/**
 * One cite anchored to a `[CITE-<id>]` marker the agent emits inline.
 * The client splits the streamed text on those markers and renders
 * `<Annotated>` chips at each anchor. `id` is opaque per-message; the
 * agent picks short, scoped ids (e.g. `s1`, `s2`).
 */
export const AskCite = z.object({
    id: z.string().min(1).max(40),
    kind: AskCiteKind,
    label: z.string().min(1).max(ASK_CITE_LABEL_MAX),
    /** Optional click-through into the source record. */
    href: z.string().optional(),
});
/** Role of an Ask message. `system` is reserved for future agent meta. */
export const AskMessageRole = z.enum(['user', 'agent', 'system']);
/**
 * One message in an Ask conversation. The `text` field stores the raw
 * verbatim content with `[CITE-<id>]` markers inline; the client
 * splits on those at render time. Agent responses carry an optional
 * `confidence` (0..1) and `interpretation` (boolean) — when present,
 * the UI surfaces `<ConfidenceMeter>` + `<Interp>` next to the text.
 *
 * `ownerUid` is denormalized from the parent `AskThread.ownerUid` so
 * Firestore rules can authorize message reads with a direct field
 * compare instead of a per-doc `get()` against the parent thread —
 * `get()` calls are capped per rules evaluation and listing many
 * messages would otherwise hit the limit.
 */
export const AskMessage = z.object({
    id: z.string().min(1),
    threadId: z.string().min(1),
    orgId: z.string().min(1),
    /**
     * UID of the human who owns this message's parent thread. Server-
     * stamped on every append (matches `AskThread.ownerUid`); identical
     * across both user and agent messages in the same thread so the
     * rules engine can do a flat `resource.data.ownerUid == auth.uid`.
     */
    ownerUid: z.string().min(1),
    /** Author of the message — server-stamped on user prompts, 'system' for agent. */
    authorUid: z.string().min(1),
    role: AskMessageRole,
    text: z.string().max(ASK_RESPONSE_MAX),
    cites: z.array(AskCite).max(ASK_CITES_MAX).default([]),
    /** Agent only: 0..1 confidence in the synthesized answer. */
    confidence: z.number().min(0).max(1).optional(),
    /** Agent only: true when the answer extrapolates beyond grounded fact. */
    interpretation: z.boolean().optional(),
    /** Agent only: model id that produced the response. */
    model: z.string().optional(),
    createdAt: z.string(),
});
/**
 * One Ask conversation. The thread is per-user within an org; a CEO and
 * their COO have independent threads. `lastMessageAt` mirrors
 * `updatedAt` semantics — bumped on every message append so the right-
 * rail thread list orders most-recent-first cheaply.
 */
export const AskThread = z.object({
    id: z.string().min(1),
    orgId: z.string().min(1),
    ownerUid: z.string().min(1),
    /** Display title — first 8 words of the first user message by default. */
    title: z.string().max(ASK_THREAD_TITLE_MAX),
    lastMessageAt: z.string(),
    createdAt: z.string(),
});
/**
 * Body for `POST /api/orgs/:o/ask`. The server creates a new thread
 * when `threadId` is absent, or appends to the supplied thread.
 * Response is SSE — see the wire-format comment at the top of the file.
 */
export const AskRequest = z.object({
    question: z.string().min(1).max(ASK_QUESTION_MAX),
    /** Optional — append to an existing thread. */
    threadId: z.string().min(1).optional(),
});
/** Wire shape for `GET /api/orgs/:o/ask/threads`. */
export const ListAskThreadsResponse = z.object({
    threads: z.array(AskThread),
});
/** Wire shape for `GET /api/orgs/:o/ask/threads/:id`. */
export const ReadAskThreadResponse = z.object({
    thread: AskThread,
    messages: z.array(AskMessage),
});
